/**
 * items を最大 concurrency 件ずつ並行して run に渡し、結果を items と同じ順で返す。
 *
 * - run が例外を投げたら、まだ始めていない要素は実行しない（実行中の要素は完了を待つ）。
 *   全要素の待ちが終わったあと、最初の例外を投げる
 * - concurrency は 1 未満なら 1 として扱う
 *
 * @param items - 処理する要素
 * @param concurrency - 同時に実行する件数の上限
 * @param run - 1 要素を処理する関数
 * @returns items と同じ順の結果
 */
export const runWithConcurrency = async <T, R>(
  items: readonly T[],
  concurrency: number,
  run: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  let failed = false;
  let firstError: unknown;

  const worker = async (): Promise<void> => {
    while (!failed && nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      try {
        results[index] = await run(items[index], index);
      } catch (error) {
        if (!failed) {
          failed = true;
          firstError = error;
        }
      }
    }
  };

  const workerCount = Math.min(Math.max(1, concurrency), items.length);
  await Promise.all(Array.from({ length: workerCount }, worker));

  if (failed) {
    throw firstError;
  }
  return results;
};
